/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { FeatureFlag } from '@ibm-watson/shared/ts/feature-flags';
import { PlanName } from '@ibm-watson/shared/ts/plan';
import {
  channelTypeToChannelName,
  AMAZON_CONNECT_BYOSD_TYPE,
  GENESYS_BYOSD_TYPE,
  NICE_INCONTACT_BYOSD_TYPE,
  TWILIO_BYOSD_TYPE,
  WEB_BYOSD_TYPE
} from '../../util/channelTypes';
import { links } from './links';

// TO-DO: Add urls when available and uncomment the lines below
export const serviceDeskOptions = [
  {
    label: channelTypeToChannelName[GENESYS_BYOSD_TYPE],
    id: GENESYS_BYOSD_TYPE,
    url: links.genesysServiceDesk
  },
  // {
  //   label: channelTypeToChannelName[INTELEPEER_BYOSD_TYPE],
  //   id: INTELEPEER_BYOSD_TYPE,
  //   url: links.intelepeerServiceDesk
  // },
  // {
  //   label: channelTypeToChannelName[NICE_INCONTACT_BYOSD_TYPE],
  //   id: NICE_INCONTACT_BYOSD_TYPE
  // },
  {
    label: channelTypeToChannelName.twilioFlex,
    id: TWILIO_BYOSD_TYPE,
    url: links.twilioServiceDesk
  },
  {
    label: channelTypeToChannelName[WEB_BYOSD_TYPE],
    id: WEB_BYOSD_TYPE,
    url: links.byoServiceDesk
  }
];

export const contactCenterOptions = [
  {
    label: channelTypeToChannelName[NICE_INCONTACT_BYOSD_TYPE],
    id: NICE_INCONTACT_BYOSD_TYPE,
    learnMore: links.learnMoreNiceCXoneLink
  },
  {
    label: channelTypeToChannelName[AMAZON_CONNECT_BYOSD_TYPE],
    id: AMAZON_CONNECT_BYOSD_TYPE,
    learnMore: links.learnMoreAmazonConnectLink
  }
];

export const intelepeerCheck = currentChannelInstance => {
  return (
    currentChannelInstance?.config?.private?.phone_numbers[0]
      ?.auto_provisioned ?? false
  );
};

export const duplicateCheck = (watchedFields, newNormalizedNum) => {
  let phoneNumbers = _.cloneDeep(watchedFields);
  phoneNumbers = phoneNumbers.slice(0, -1);
  return (
    phoneNumbers.find(
      phoneNumberItem => phoneNumberItem.phone_number === newNormalizedNum
    ) !== undefined
  );
};

// Phone integration is only allowed for plus, plus trial, enterprise, and enterprise data isolation instances
// Phone integration is also allowed when it is enabled by App Config in premium plans
export const phoneEnabled = (plan, features) => {
  return (
    !isCpd &&
    (plan === PlanName.DEV_ONLY_PREMIUM ||
      plan === PlanName.PLUS ||
      plan === PlanName.PLUS_TRIAL ||
      plan === PlanName.ENTERPRISE ||
      plan === PlanName.ENTERPRISE_DATA_ISOLATION ||
      features[FeatureFlag.PHONE])
  );
};

const readFileToArrayBuffer = fileData => {
  const fileReader = new FileReader();

  return new Promise((resolve, reject) => {
    fileReader.onload = () => {
      const arrayBuffer = fileReader.result;
      resolve(arrayBuffer);
    };

    fileReader.onerror = () => {
      fileReader.abort();
      reject(new Error('failed to process file'));
    };

    // Initiate the conversion.
    fileReader.readAsArrayBuffer(fileData);
  });
};

interface VisualizationDataPayload {
  duration: number;
  reducedFloatArray: number[];
}

export const convertAudioBlobToVisualizationData = async (
  audioBlob,
  audioCtx
): Promise<VisualizationDataPayload> => {
  const audioArrayBuffer = await readFileToArrayBuffer(audioBlob);
  const audioUint8Array = new Uint8Array((audioArrayBuffer as any[]).slice(0));

  // NOTE: BaseAudioContext.decodeAudioData has a promise syntax
  // which we are unable to use in order to be compatible with Safari.
  // Therefore, we wrap the callback syntax in a promise to give us the same
  // effect while ensuring compatibility
  // see more: https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/decodeAudioData#Browser_compatibility
  return new Promise((resolve, reject) => {
    audioCtx.decodeAudioData(
      audioArrayBuffer,
      audioDataBuffer => {
        const { duration } = audioDataBuffer;

        const numberOfChunks = 55;
        const chunkSize = audioUint8Array.length / numberOfChunks;
        const chunkedAudioDataArray = [];
        for (let i = 1; i < numberOfChunks; i += 1) {
          let previousIndex = i - 1;
          if (previousIndex < 0) {
            previousIndex = 0;
          }

          chunkedAudioDataArray.push(
            audioUint8Array.slice(previousIndex * chunkSize, i * chunkSize)
          );
        }

        const reducedFloatArray = chunkedAudioDataArray.map(chunk => {
          const totalValue = chunk.reduce(
            (prevValue, currentValue) => prevValue + currentValue
          );
          const floatValue = totalValue / (chunkSize * 255);
          return floatValue;
        });

        resolve({
          duration,
          reducedFloatArray
        });
      },
      () => {
        reject(new Error('failed to chunk audio'));
      }
    );
  });
};

/**
 * Removes the first speech instance that is auto-provisioned.
 * The phoneNumbers array is edited in-place.
 */
export const removeAutoProvisionedNumber = async (
  phoneNumbers: any[]
): Promise<boolean> => {
  const autoProvisionedNumberIndex = phoneNumbers
    // map to an array of booleans (true === auto_provisioned)
    .map(numberObject => numberObject?.auto_provisioned)
    // index of first item of `true`
    .indexOf(true);

  if (autoProvisionedNumberIndex > -1) {
    phoneNumbers.splice(autoProvisionedNumberIndex, 1);
    return true;
  }

  return false;
};

/**
 * This regex is capturing two groups:
 * 1. Everything before '/genesysaudioconnector' (including '/genesysaudioconnector').
 * 2. Everything after '/genesysaudioconnector'.
 *
 * For example, if you apply this regex to the string "example/genesysaudioconnector/foo/bar"
 * it will capture "example/genesysaudioconnector" in the first group and "foo/bar" in the second group.
 * This is used in Genesys audio connector connection type in Phone
 */
export const genesysAudioConnectorURIRegExMatch =
  /(.*?\/genesysaudioconnector)\/(.*)/;
